"use strict";
//
// Copyright © 2020 Anticrm Platform Contributors.
//
// Licensed under the Eclipse Public License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License. You may
// obtain a copy of the License at https://www.eclipse.org/legal/epl-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
//
// See the License for the specific language governing permissions and
// limitations under the License.
//
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.ClientConnectEvent = void 0;
exports.createClient = createClient;
exports.buildModel = buildModel;
const analytics_1 = require("@hcengineering/analytics");
const classes_1 = require("./classes");
const component_1 = __importDefault(require("./component"));
const hierarchy_1 = require("./hierarchy");
const measurements_1 = require("@hcengineering/measurements");
const memdb_1 = require("./memdb");
const tx_1 = require("./tx");
const utils_1 = require("./utils");
/**
 * @public
 */
var ClientConnectEvent;
(function (ClientConnectEvent) {
    ClientConnectEvent[ClientConnectEvent["Connected"] = 0] = "Connected";
    ClientConnectEvent[ClientConnectEvent["Reconnected"] = 1] = "Reconnected";
    // Client could cause back a few more states.
    ClientConnectEvent[ClientConnectEvent["Upgraded"] = 2] = "Upgraded";
    ClientConnectEvent[ClientConnectEvent["Refresh"] = 3] = "Refresh";
    ClientConnectEvent[ClientConnectEvent["Maintenance"] = 4] = "Maintenance"; // In case workspace are in maintenance mode
})(ClientConnectEvent || (exports.ClientConnectEvent = ClientConnectEvent = {}));
class ClientImpl {
    conn;
    ctx;
    notify;
    hierarchy;
    model;
    appliedModelTransactions = new Set();
    constructor(conn, ctx) {
        this.conn = conn;
        this.ctx = ctx;
    }
    getConnection() {
        return this.conn;
    }
    setModel(hierarchy, model) {
        this.hierarchy = hierarchy;
        this.model = model;
    }
    getHierarchy() {
        return this.hierarchy;
    }
    getModel() {
        return this.model;
    }
    async findAll(_class, query, options) {
        const domain = this.hierarchy.getDomain(_class);
        const data = domain === classes_1.DOMAIN_MODEL
            ? await this.model.findAll(_class, query, options)
            : await this.conn.findAll(_class, query, options);
        // In case of mixin we need to create mixin proxies.
        // Update mixins & lookups
        const result = data.map((v) => {
            return this.hierarchy.updateLookupMixin(_class, v, options);
        });
        return (0, utils_1.toFindResult)(result, data.total);
    }
    async searchFulltext(query, options) {
        return await this.conn.searchFulltext(query, options);
    }
    async domainRequest(ctx, params, options) {
        return await this.conn.domainRequest(ctx, params, options);
    }
    async findOne(_class, query, options) {
        return (await this.findAll(_class, query, { ...options, limit: 1 }))[0];
    }
    async tx(tx) {
        if (tx.objectSpace === component_1.default.space.Model) {
            // Synchronous and one shared instance for model and hierarchy, as ModelMiddleware.tx on the server.
            this.model.addTxes(this.ctx, [tx], true);
            this.appliedModelTransactions.add(tx._id);
        }
        // We need to handle it on server, before performing local live query updates.
        const result = await this.conn.tx(tx);
        // The connection notifies local tx handlers (live queries) without awaiting them.
        // Give them a turn before the caller issues the next tx, or their updates are coalesced.
        await Promise.resolve();
        return result;
    }
    async updateFromRemote(...tx) {
        for (const t of tx) {
            try {
                if (t.objectSpace === component_1.default.space.Model) {
                    const hasTx = this.appliedModelTransactions.has(t._id);
                    if (!hasTx) {
                        this.model.addTxes(this.ctx, [t], true);
                    }
                    else {
                        this.appliedModelTransactions.delete(t._id);
                    }
                }
            }
            catch (err) {
                // console.error('failed to apply model transaction, skipping', t)
                continue;
            }
        }
        this.notify?.(...tx);
    }
    async close() {
        await this.conn.close();
    }
    async loadChunk(domain, idx) {
        return await this.conn.loadChunk(domain, idx);
    }
    async getDomainHash(domain) {
        return await this.conn.getDomainHash(domain);
    }
    async closeChunk(idx) {
        await this.conn.closeChunk(idx);
    }
    async loadDocs(domain, docs) {
        return await this.conn.loadDocs(domain, docs);
    }
    async upload(domain, docs) {
        await this.conn.upload(domain, docs);
    }
    async clean(domain, docs) {
        await this.conn.clean(domain, docs);
    }
    async sendForceClose() {
        await this.conn.sendForceClose();
    }
}
/**
 * @public
 */
async function createClient(connect, 
// If set will build model with only allowed plugins.
modelFilter, txPersistence, _ctx) {
    const ctx = _ctx ?? new measurements_1.MeasureMetricsContext('createClient', {});
    let client = null;
    // Temporal buffer, while we apply model
    let txBuffer = [];
    let hierarchy = new hierarchy_1.Hierarchy();
    let model = new memdb_1.ModelDb(hierarchy);
    let lastTx;
    function txHandler(...tx) {
        if (tx == null || tx.length === 0) {
            return;
        }
        if (client === null) {
            txBuffer?.push(...tx);
        }
        else {
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            client.updateFromRemote(...tx);
        }
        for (const t of tx) {
            if (t._class === component_1.default.class.TxWorkspaceEvent && t.event === tx_1.WorkspaceEvent.LastTx) {
                lastTx = t.params.lastTx;
            }
        }
    }
    const conn = await ctx.with('connect', {}, () => connect(txHandler), {}, { suspendErrors: true });
    let { mode, current, addition } = await ctx.with('load-model', {}, (ctx) => loadModel(ctx, conn, txPersistence));
    switch (mode) {
        case 'same':
        case 'upgrade':
            ctx.withSync('build-model', {}, (ctx) => {
                buildModel(ctx, current, modelFilter, hierarchy, model);
            });
            break;
        case 'addition':
            ctx.withSync('build-model', {}, (ctx) => {
                buildModel(ctx, current.concat(addition), modelFilter, hierarchy, model);
            });
    }
    current = [];
    addition = [];
    txBuffer = txBuffer.filter((tx) => tx.space !== component_1.default.space.Model);
    client = new ClientImpl(conn, ctx);
    client.setModel(hierarchy, model);
    txHandler(...txBuffer);
    txBuffer = undefined;
    const oldOnConnect = conn.onConnect;
    conn.onConnect = async (event, _lastTx, data) => {
        console.log('Client: onConnect', event);
        if (event === ClientConnectEvent.Maintenance) {
            lastTx = _lastTx;
            await oldOnConnect?.(ClientConnectEvent.Maintenance, _lastTx, data);
            return;
        }
        // Find all new transactions and apply
        let { mode, current, addition } = await ctx.with('load-model', {}, (ctx) => loadModel(ctx, conn, txPersistence));
        switch (mode) {
            case 'upgrade':
                // We have upgrade procedure and need rebuild all stuff.
                hierarchy = new hierarchy_1.Hierarchy();
                model = new memdb_1.ModelDb(hierarchy);
                client.setModel(hierarchy, model);
                ctx.withSync('build-model', {}, (ctx) => {
                    buildModel(ctx, current, modelFilter, hierarchy, model);
                });
                current = [];
                await oldOnConnect?.(ClientConnectEvent.Upgraded, _lastTx, data);
                // No need to fetch more stuff since upgrade was happened.
                break;
            case 'addition':
                ctx.withSync('build-model', {}, (ctx) => {
                    buildModel(ctx, current.concat(addition), modelFilter, hierarchy, model);
                });
                break;
        }
        current = [];
        addition = [];
        if (lastTx === undefined) {
            // No need to do anything here since we connected.
            await oldOnConnect?.(event, _lastTx, data);
            lastTx = _lastTx;
            return;
        }
        if (lastTx === _lastTx) {
            // Same lastTx, no need to refresh
            await oldOnConnect?.(ClientConnectEvent.Reconnected, _lastTx, data);
            return;
        }
        lastTx = _lastTx;
        // We need to trigger full refresh on queries, etc.
        await oldOnConnect?.(ClientConnectEvent.Refresh, lastTx, data);
    };
    return client;
}
async function loadModel(ctx, conn, persistence) {
    const t = (0, utils_1.platformNow)();
    const current = (await ctx.with('persistence-load', {}, () => persistence?.load())) ?? {
        full: true,
        transactions: [],
        hash: ''
    };
    if (conn.getLastHash !== undefined && (await conn.getLastHash(ctx)) === current.hash) {
        // We have same model hash.
        return { mode: 'same', current: current.transactions, addition: [] };
    }
    const lastTxTime = getLastTxTime(current.transactions);
    const result = await ctx.with('connection-load-model', { hash: current.hash !== '' }, (ctx) => conn.loadModel(lastTxTime, current.hash));
    if (Array.isArray(result)) {
        // Fallback to old behavior, only for tests
        return {
            mode: 'same',
            current: result,
            addition: []
        };
    }
    // Save concatenated, if have some more of them.
    void ctx
        .with('persistence-store', {}, (ctx) => persistence?.store({
        ...result,
        // Store concatinated old + new txes
        transactions: result.full ? result.transactions : current.transactions.concat(result.transactions)
    }))
        .catch((err) => {
        analytics_1.Analytics.handleError(err);
    });
    if (typeof window !== 'undefined') {
        console.log('find' + (result.full ? 'full model' : 'model diff'), result.transactions.length, (0, utils_1.platformNowDiff)(t));
    }
    if (result.full) {
        return { mode: 'upgrade', current: result.transactions, addition: [] };
    }
    return { mode: 'addition', current: current.transactions, addition: result.transactions };
}
function buildModel(ctx, transactions, modelFilter, hierarchy, model) {
    let txes = transactions;
    if (modelFilter !== undefined) {
        txes = modelFilter(txes);
    }
    // addTxes feeds the hierarchy itself, a separate pass would just deserialize everything twice.
    ctx.withSync('build model', {}, (ctx) => {
        model.addTxes(ctx, txes, false);
    });
}
function getLastTxTime(txes) {
    let lastTxTime = 0;
    for (const tx of txes) {
        if (tx.modifiedOn > lastTxTime) {
            lastTxTime = tx.modifiedOn;
        }
    }
    return lastTxTime;
}
//# sourceMappingURL=client.js.map